import { useEffect, useRef } from 'react';
/**
 * `MultiSelectCheckbox` renders a group of checkboxes with an optional
 * "Select all" control.
 *
 * Behaviour:
 * - `value` is a list of selected option values; changes are emitted via
 *   `onChange(nextValues)`.
 * - When `selectAllLabel` is provided, a top-level checkbox toggles all options.
 * - The "Select all" checkbox uses the native `indeterminate` state when some
 *   (but not all) options are selected.
 * - Individual options toggle membership in `value`.
 * - When `disabled` is true, all checkboxes are disabled.
 */

import Checkbox from '../Checkbox/Checkbox';

export default function MultiSelectCheckbox({
  options,
  value = [],
  label,
  selectAllLabel,
  error,
  onChange,
  disabled
}: Readonly<{
  options: { value: string; label: string }[];
  value: string[];
  label?: string;
  selectAllLabel?: string;
  error?: string;
  onChange: (value: string[]) => void;
  disabled?: boolean;
}>) {
  const allValues = options.map((o) => o.value);
  const allSelected =
    allValues.length > 0 && allValues.every((v) => value.includes(v));
  const noneSelected = value.length === 0;

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = !allSelected && !noneSelected;
  }, [allSelected, noneSelected]);

  const toggle = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(allValues);
    }
  };

  const optionWrapperClass = `${selectAllLabel ? 'ms-3' : ''}`;

  return (
    <fieldset>
      <legend className='control-label control-label--group'>{label}</legend>

      {selectAllLabel && (
        <Checkbox
          ref={selectAllRef}
          id='ms-select-all'
          checked={allSelected}
          text={selectAllLabel}
          onChange={toggleAll}
          disabled={disabled}
        />
      )}

      {options.map((opt) => {
        const id = `ms-${opt.value}`;
        const checked = value.includes(opt.value);

        return (
          <div key={opt.value} className={optionWrapperClass}>
            <Checkbox
              id={id}
              checked={checked}
              text={opt.label}
              onChange={() => toggle(opt.value)}
              disabled={disabled}
            />
          </div>
        );
      })}
      {error && <div className='error-style'>{error}</div>}
    </fieldset>
  );
}
